import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";
import Navbar from "../../../../globals/components/Navbar";
import {
  GoogleAutoComplete,
  PLACES_TYPES_GEOCODE,
} from "../../../../globals/components/forms/GoogleAutoCompleteField";
import { normalizeUsState, isAllowedUsState } from "../../../../globals/utils/usStates";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

const ObligeeListGoogleAutoComplete = (props) => (
  <GoogleAutoComplete pacZIndex={13000} placesTypes={PLACES_TYPES_GEOCODE} {...props} />
);

const ObligeeList = () => {
  const [obligees, setObligees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editObligeeId, setEditObligeeId] = useState(null);

  const [name, setName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");

  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchObligees(), loadCityStateDropdowns()]);
      setLoading(false);
    };
    load();
  }, []);

  const fetchObligees = async () => {
    try {
      const data = await apiClient("/obligee-list-create/");
      setObligees(data);
    } catch (err) {
      toast.error("Failed to load obligees");
    }
  };

  const loadCityStateDropdowns = async () => {
    try {
      setAllCities(await apiClient("/fields/city/"));
      setAllStates(await apiClient("/fields/states/"));
    } catch (err) {
      toast.error("Failed to load city/state options");
    }
  };

  const resetForm = () => {
    setName(""); setStreet(""); setCity(""); setState(""); setZip("");
  };

  const handleGoogleCallback = (a, c, s, z) => {
    setStreet(a);
    setZip(z);
    const matchedCity = allCities.find(x => x.toLowerCase() === c.toLowerCase());
    const normalizedState = normalizeUsState(s, allStates);
    const matchedState = allStates.find(x => x.toLowerCase() === normalizedState.toLowerCase());
    setCity(matchedCity || c);
    setState(matchedState || normalizedState);
  };

  const handleAddObligee = async () => {
    setCreating(true);
    try {
      const normalizedState = normalizeUsState(state, allStates);
      if (normalizedState && !isAllowedUsState(normalizedState, allStates)) {
        toast.error(`Invalid state "${state}". Please select a valid state.`);
        return;
      }
      await apiClient("/obligee-list-create/", "POST", {
        obligee_name: name,
        obligee_address: street,
        obligee_city: city,
        obligee_state: normalizedState,
        obligee_zip: zip,
      });
      toast.success("Obligee added");
      setOpen(false);
      resetForm();
      fetchObligees();
    } catch {
      toast.error("Failed to add obligee");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateObligee = async () => {
    setUpdating(true);
    try {
      const normalizedState = normalizeUsState(state, allStates);
      if (normalizedState && !isAllowedUsState(normalizedState, allStates)) {
        toast.error(`Invalid state "${state}". Please select a valid state.`);
        return;
      }
      await apiClient(`/obligee-detail-update/${editObligeeId}/`, "PATCH", {
        obligee_id: editObligeeId,
        obligee_name: name,
        obligee_address: street,
        obligee_city: city,
        obligee_state: normalizedState,
        obligee_zip: zip,
      });
      toast.success("Obligee updated");
      setEditOpen(false);
      resetForm();
      fetchObligees();
    } catch {
      toast.error("Failed to update obligee");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteObligee = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      await apiClient(`/obligee-detail-update/${deletingId}/`, "DELETE");
      toast.success("Obligee deleted successfully.");
      setDeleteOpen(false);
      setDeletingId(null);
      fetchObligees();
    } catch (err) {
      if (err?.status === 404) {
        toast.error("Obligee not found");
      } else {
        toast.error("Failed to delete obligee");
      }
    } finally {
      setDeleting(false);
    }
  };

  const filtered = obligees.filter(o => {
    const q = searchTerm.toLowerCase();
    return (
      o.obligee_name?.toLowerCase().includes(q) ||
      o.obligee_address?.toLowerCase().includes(q) ||
      o.obligee_state?.toLowerCase().includes(q) ||
      o.obligee_zip?.toLowerCase().includes(q)
    );
  });
  const currentRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Navbar />
      <Box sx={{ padding: 3 }}>
        <Typography variant="h4" fontWeight="bold" mb={2}>Obligees</Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", mb: 2, gap: 2 }}>
        <TextField
            label="Search by Name"
            placeholder="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}}
            sx={{
              width: "300px",
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <Button
            variant="contained"
            sx={{ backgroundColor: "#1976d2", color: "white", textTransform: "uppercase" }}
            onClick={() => setOpen(true)}
          >
            Add Obligee
          </Button>
        </Box>

        {loading ? <Box display="flex" justifyContent="center"><CircularProgress /></Box> : (
          <>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Street</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Zip</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentRows.map((o, i) => (
                    <TableRow key={i}>
                      <TableCell>{o.obligee_name}</TableCell>
                      <TableCell>{o.obligee_address}</TableCell>
                      <TableCell>{o.obligee_city}</TableCell>
                      <TableCell>{o.obligee_state}</TableCell>
                      <TableCell>{o.obligee_zip}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => {
                          setEditObligeeId(o.obligee_id);
                          setName(o.obligee_name || "");
                          setStreet(o.obligee_address || "");
                          setCity(o.obligee_city || "");
                          setState(o.obligee_state || "");
                          setZip(o.obligee_zip || "");
                          setEditOpen(true);
                        }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => {
                          setDeletingId(o.obligee_id);
                          setDeleteOpen(true);
                        }}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
          ActionsComponent={TablePaginationActions}
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Box>

      {/* Add Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add Obligee</DialogTitle>
        <DialogContent sx={{ width: 400 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <ObligeeListGoogleAutoComplete placeholder="Search Address" callback={handleGoogleCallback} />
          <TextField label="Street Address" value={street} onChange={(e) => setStreet(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <Autocomplete options={allCities} value={city} onChange={(e, val) => setCity(val)} renderInput={(params) => <TextField {...params} label="City" fullWidth />} />
          <Autocomplete options={allStates} value={state} onChange={(e, val) => setState(val)} renderInput={(params) => <TextField {...params} label="State" fullWidth sx={{ mt: 1 }} />} />
          <TextField label="ZIP" value={zip} onChange={(e) => setZip(e.target.value)} fullWidth sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleAddObligee} variant="contained" disabled={creating}>
            {creating ? <CircularProgress size={24} /> : "Add Obligee"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Obligee</DialogTitle>
        <DialogContent sx={{ width: 400 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <ObligeeListGoogleAutoComplete placeholder="Search Address" callback={handleGoogleCallback} />
          <TextField label="Street Address" value={street} onChange={(e) => setStreet(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <Autocomplete options={allCities} value={city} onChange={(e, val) => setCity(val)} renderInput={(params) => <TextField {...params} label="City" fullWidth />} />
          <Autocomplete options={allStates} value={state} onChange={(e, val) => setState(val)} renderInput={(params) => <TextField {...params} label="State" fullWidth sx={{ mt: 1 }} />} />
          <TextField label="ZIP" value={zip} onChange={(e) => setZip(e.target.value)} fullWidth sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateObligee} variant="contained" disabled={updating}>
            {updating ? <CircularProgress size={24} /> : "Update Obligee"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Obligee</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this obligee? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteObligee} variant="contained" color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ObligeeList;
